import { motion } from "framer-motion";
import { Reveal } from "../site/Reveal";
import { PreferenceChart, AromaChart, NoPreferenceNote } from "../site/Charts";
import { ChartCard } from "./Shared";
import { FlavourCompare } from "./FlavourCompare";
import { FOOTNOTE } from "../../data/products";

const Variant = ({ v, i, accent }) => (
  <motion.article
    data-testid={`variant-card-${v.code}`}
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount: 0.1 }}
    transition={{ duration: 0.7, delay: (i % 3) * 0.08, ease: [0.22, 1, 0.36, 1] }}
    whileHover={{ y: -6 }}
    className="group relative overflow-hidden rounded-[24px] border border-[var(--line)] bg-[var(--paper)] p-7 flex flex-col min-h-[300px]"
  >
    <span className="absolute -right-10 -top-10 h-40 w-40 rounded-full opacity-80 transition-transform duration-700 group-hover:scale-125" style={{ background: v.swatch }} />
    <p className="relative font-display text-6xl font-light" style={{ color: accent }}>{v.code}</p>
    <p className="relative font-display text-2xl text-[var(--forest)] mt-3">{v.name}</p>
    <p className="relative text-sm text-[var(--muted)] mt-1">{v.notes}</p>
    <div className="relative mt-auto pt-6 border-t border-[var(--line)]">
      <p className="font-mono text-[11px] uppercase tracking-widest text-[var(--muted)]">Best for</p>
      <p className="text-[var(--ink)] mt-1">{v.best}</p>
      <p className="font-mono text-sm mt-3" style={{ color: accent }}>{v.dose}</p>
    </div>
  </motion.article>
);

export const PalativaDetail = ({ r }) => (
  <>
    <section className="mx-auto max-w-[1400px] px-5 md:px-10 pb-20 md:pb-28">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
        <div>
          <p className="eyebrow" style={{ color: r.accent }}>The range</p>
          <h2 className="font-display text-4xl md:text-6xl font-light text-[var(--forest)] mt-4">Six sensory profiles.</h2>
        </div>
        <p className="text-[var(--muted)] max-w-md">From fruity banana to fresh milky notes — match the flavour to the feed, add it early, and mix it well.</p>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {r.variants.map((v, i) => <Variant key={v.code} v={v} i={i} accent={r.accent} />)}
      </div>
    </section>

    <FlavourCompare r={r} />

    <section className="bg-[var(--forest)] text-[var(--bone)] grain relative overflow-hidden">
      <div className="mx-auto max-w-[1400px] px-5 md:px-10 py-20 md:py-28 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <p className="eyebrow text-[#8DC63F]">When intake is at risk</p>
          <h2 className="font-display text-4xl md:text-5xl font-light mt-4 leading-tight">{r.intro.split(".")[0]}.</h2>
          <p className="text-[#C9D6C8] mt-6 leading-relaxed">{r.intro.split(".").slice(1).join(".").trim()}</p>
        </div>
        <ul className="lg:col-span-7 grid sm:grid-cols-2 gap-3">
          {r.risks.map((x, i) => (
            <Reveal as="li" key={x} delay={i * 0.05} className="rounded-2xl border border-white/15 p-5 flex gap-4 items-start">
              <span className="font-mono text-xs text-[#8DC63F] pt-1">0{i + 1}</span>
              <span className="text-lg">{x}</span>
            </Reveal>
          ))}
        </ul>
      </div>
    </section>

    <section className="mx-auto max-w-[1400px] px-5 md:px-10 py-20 md:py-28">
      <div className="grid lg:grid-cols-12 gap-5">
        <ChartCard className="lg:col-span-7" tag="Two-choice test, share of total intake*" title="Animals choose PALATIVA feed" color={r.accent}>
          <PreferenceChart h={300} /><NoPreferenceNote />
        </ChartCard>
        <ChartCard className="lg:col-span-5" tag="Aroma retained after conditioning, %*" title="Aroma that survives pelleting" color={r.accent2}>
          <AromaChart h={300} />
        </ChartCard>
      </div>
      <p className="mt-5 text-xs text-[var(--muted)] max-w-3xl">{FOOTNOTE}</p>
    </section>

    <section className="mx-auto max-w-[1400px] px-5 md:px-10 pb-8 grid lg:grid-cols-12 gap-12">
      <div className="lg:col-span-5">
        <p className="eyebrow" style={{ color: r.accent }}>Applications</p>
        <h2 className="font-display text-4xl font-light text-[var(--forest)] mt-4">Where PALATIVA works</h2>
        <div className="mt-8 flex flex-wrap gap-2">
          {r.applications.map((a) => <span key={a} className="rounded-full border border-[var(--line)] bg-[var(--paper)] px-4 py-2 text-sm">{a}</span>)}
        </div>
      </div>
      <div className="lg:col-span-7">
        <p className="eyebrow" style={{ color: r.accent }}>How to use</p>
        <div className="mt-6 grid sm:grid-cols-3 gap-5">
          {r.howto.map((h, i) => (
            <Reveal key={h.t} delay={i * 0.08} className="rounded-2xl p-6" style={{ background: r.tint }}>
              <p className="font-display text-5xl font-light" style={{ color: r.accent }}>{i + 1}</p>
              <p className="font-display text-xl text-[var(--forest)] mt-4">{h.t}</p>
              <p className="text-sm text-[var(--muted)] mt-2 leading-relaxed">{h.d}</p>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  </>
);
